import * as types from '../../mutation-types';
import settings from '@/settings';
import moment from 'moment';
import debounce from 'lodash-es/debounce';
import store from '@/store';
import { updateAbility } from '@/ability';
import router from '../../../router';
import {
    startOfMinute,
    startOfDay,
    endOfDay,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    calculateCompletedRangeCount
} from '@/utils';

import { EventBus } from '@/event-bus';
import memomapsDataHelper from '../module-course/memomaps-data-helper';
import repetitionHelper from './repetition-helper';
import scenarioHelper from './scenario-helper';
import persistence from '@/store/persistence';
import axios from 'axios';
import apolloClient from '@/apollo-client';
import gql from 'graphql-tag';
import CreateBraindateQ from './CreateBraindate.gql';
import BraindatesList from './BraindatesList.gql';
import CreateOrUpdateVideoProgress from './CreateOrUpdateVideoProgress.gql';

let timerIntervalId = null;
let startOfMinuteUnwatch = null;

export default {
    async initApp(store) {
        await persistence.loadApp();
        scenarioHelper.configure(store);
    },
    async activateApp({ dispatch, commit, state, rootState }) {
        dispatch('startTimer');
        if (rootState.moduleAuth.isAuthenticated) {
            repetitionHelper.activate();
        }

        await dispatch('maybeReloadApp');
    },
    deactivateApp({ dispatch }) {
        dispatch('stopTimer');
        repetitionHelper.deactivate();
        if (startOfMinuteUnwatch) {
            startOfMinuteUnwatch();
        }
    },
    loadBraindates({ commit }) {
        apolloClient
            .query({
                fetchPolicy: 'no-cache',
                query: BraindatesList,
                variables: { userId: store.getters['moduleAuth/profile'].id }
            })
            .then(result => {
                result.data.braindatesList.forEach(bd => {
                    if (bd.language === 'isBonus') {
                        bd.isBonus = true;
                    }
                });
                commit(types.SET_BRAINDATES, result.data.braindatesList);
                commit(types.SET_MEMOSTREAK, result.data.memostreak);
            })
            .catch(err => {
                console.error(err); // eslint-disable-line no-console
            });
    },
    async cleanupAfterUserLogout() {
        await apolloClient.resetStore();
    },

    async loadUser({ commit, state, dispatch, rootState }) {
        const sourceLang = state.memolife.appLanguage === 'no' ? 'norwegian' : 'english';
        const similarityListPattern = /-(\w+)-\w+-/;
        await apolloClient.resetStore();
        dispatch('loadBraindates');
        dispatch('loadLifekeys');
        await dispatch('loadSettings');

        function handleStepGroupRepetitionEvent(event) {
            commit(
                'moduleCourse/' + types.UPDATE_STEP_GROUP_REPETITION,
                {
                    stepGroupId: event.data.itemId,
                    repetition: event.data,
                    now: Date.now(),
                    clear: event.type === 'deleted'
                },
                { root: true }
            );
        }
        function handleLanguageRepetitionEvent(event) {
            const languageId = event.data.namespace.substr('language-'.length);
            const listId = event.data.itemId;
            const m = similarityListPattern.exec(listId);
            if (m && m[1] !== sourceLang) {
                // NOTE: Ignoring lists with specified sourceLang different from appLanguage
                return;
            }
            commit(types.UPDATE_LANGUAGE_REPETITION, {
                languageId,
                listId,
                repetition: event.data,
                now: Date.now(),
                clear: event.type === 'deleted'
            });
        }

        dispatch(
            'moduleSocial/configure',
            { userId: rootState.moduleAuth.profile._id, email: rootState.moduleAuth.profile.email },
            { root: true }
        );

        await dispatch('maybeReloadApp');
        await dispatch('loadLifeskillsList');
        dispatch('loadFavorites');
        await dispatch('moduleCourse/loadCoursesList', null, { root: true });

        await persistence.loadUser();
        dispatch('moduleCourse/loadCourseProgress', null, { root: true });
        dispatch('moduleMemogenius/init', null, { root: true });

        repetitionHelper.on('changed', event => {
            if (event.data.namespace === 'memomaps-step-group') {
                handleStepGroupRepetitionEvent(event);
            } else if (event.data.namespace.startsWith('language-')) {
                handleLanguageRepetitionEvent(event);
            }
        });
        repetitionHelper.load(store.state.moduleCourse.progressMapping);
        startOfMinuteUnwatch = store.watch(
            () => store.state.moduleApp.time.startOfCurrentMinute,
            time => {
                Object.keys(rootState.moduleCourse.stepGroups).forEach(stepGroupId => {
                    commit(
                        'moduleCourse/' + types.UPDATE_STEP_GROUP_REPETITION,
                        {
                            stepGroupId: stepGroupId,
                            now: Date.now()
                        },
                        { root: true }
                    );
                });

                dispatch('updateAppVersionInfo');
            },
            { immediate: true }
        );
    },
    async maybeReloadApp({ dispatch, state }) {
        await dispatch('updateAppVersionInfo');
        if (state.clientsideGitRevision !== state.serversideGitRevision) {
            location.reload();
        }
    },
    async updateAppVersionInfo({ commit, state }) {
        try {
            const mf = await axios.get(`${process.env.BASE_URL}app-version.json?t=${Date.now()}`);
            if (state.serversideGitRevision === 'NEWER') {
                // NOTE: used for testing. See debug menu
                return;
            }
            commit('updateAppVersionInfo', { gitRevision: mf.data.GIT_REVISION });
        } catch (err) {
            // Maybe report to sentry?
        }
    },
    async unloadUser() {
        await persistence.unloadUser();
    },
    async setLocaleNoReload({ commit, dispatch, rootState }, language) {
        commit(types.SET_LOCALE, language);

        if (rootState.moduleAuth.isAuthenticated) {
            await dispatch('moduleAuth/updateMe', { language }, { root: true });
        }
    },
    async setLocale({ commit, dispatch, rootState }, language) {
        commit(types.SET_LOCALE, language);

        if (rootState.moduleAuth.isAuthenticated) {
            await dispatch('moduleAuth/updateMe', { language }, { root: true });
            // NOTE: we reload to be on the safe side. Might not be needed in all cases

            router.replace('/home');

            location.reload();
        }
    },
    updateTime({ state, commit }) {
        const time = state.time;
        const now = Date.now();
        const payload = {};
        const startOfCurrentMinute = startOfMinute(now);
        if (time.startOfCurrentMinute !== startOfCurrentMinute) {
            payload.startOfCurrentMinute = startOfCurrentMinute;

            const startOfCurrentDay = startOfDay(now);
            if (time.startOfCurrentDay !== startOfCurrentDay) {
                payload.startOfCurrentDay = startOfCurrentDay;
                payload.endOfCurrentDay = endOfDay(now);

                const startOfCurrentWeek = startOfWeek(now);
                if (time.startOfCurrentWeek !== startOfCurrentWeek) {
                    payload.startOfCurrentWeek = startOfCurrentWeek;
                    payload.endOfCurrentWeek = endOfWeek(now);

                    const startOfCurrentMonth = startOfMonth(now);
                    if (time.startOfCurrentMonth !== startOfCurrentMonth) {
                        payload.startOfCurrentMonth = startOfCurrentMonth;
                        payload.endOfCurrentMonth = endOfMonth(now);
                    }
                }
            }
            commit(types.UPDATE_TIME, payload);
        }
    },
    startTimer({ dispatch }) {
        if (!timerIntervalId) {
            dispatch('updateTime');
            timerIntervalId = setInterval(() => {
                dispatch('updateTime');
            }, 1000 * 1);
        }
    },
    stopTimer({ commit }) {
        clearInterval(timerIntervalId);
        timerIntervalId = null;
    },
    setAutoplayVideoParts({ commit, dispatch }, value) {
        commit(types.UPDATE_AUTOPLAY_VIDEO_PARTS, value);
        dispatch('updateSetting', { key: 'autoplayVideoParts', value });
    },
    markGetStartedPopupAsShown({ commit, dispatch }, payload) {
        const done = !payload || payload.reset !== true;
        commit(types.UPDATE_GET_STARTED_SHOWN, { done });
        dispatch('updateSetting', { key: 'didShowGetStartedPopup', value: done });
    },

    markFavoriteLifeskillsSelected({ commit, dispatch }, payload) {
        const done = !payload || payload.reset !== true;
        commit(types.UPDATE_DASHBOARD_FAVORITES_SELECTED, { done });
        dispatch('updateSetting', { key: 'didSelectFavoriteLifeskills', value: done });
    },
    markDashboardOnboardingAsShown({ commit, dispatch }, payload) {
        const done = !payload || payload.reset !== true;
        commit(types.UPDATE_DASHBOARD_ONBOARDING_SHOWN, { done });
        dispatch('updateSetting', { key: 'didShowDashboardOnboarding', value: done });
    },
    markMemomapsOnboardingAsShown({ commit, dispatch }, payload) {
        const done = !payload || payload.reset !== true;
        commit(types.UPDATE_MEMOMAPS_ONBOARDING_SHOWN, { done });
        dispatch('updateSetting', { key: 'didShowMemomapsOnboardingPopup', value: done });
    },
    markMemoOnboardingAsShown({ commit, dispatch }, payload) {
        const done = !payload || payload.reset !== true;
        commit(types.UPDATE_MEMO_ONBOARDING_SHOWN, { done });
        dispatch('updateSetting', { key: 'didShowMemoOnboardingPopup', value: done });
    },
    markDidExitGettingStartedFocus({ commit, dispatch }, payload) {
        const done = !payload || payload.reset !== true;
        commit(types.UPDATE_DID_EXIT_GETTING_STARTED_FOCUS, { done });
        dispatch('updateSetting', { key: 'didExitGettingStartedFocus', value: done });
    },
    updateWhatsNewViewedArticles({ commit, dispatch, state }, { id = null, ids = [] }) {
        if (id) {
            const prevIds = state.memolife.whatsNewViewedArticles;
            if (prevIds.indexOf(id) !== -1) {
                return;
            }
            ids = [...prevIds, id];
        }
        commit(types.UPDATE_WHATSNEW_VIEWED_ARTICLES, { ids });
        dispatch('updateSetting', { key: 'whatsNewViewedArticles', value: ids });
    },

    updateProgressOnVideo({ commit, state, dispatch }, { id, value, lifeskillId }) {
        const video = state.videos[id];
        const prevValue = video.progress;
        if (video.duration - value < 2) {
            // Note: Record as completed if less than 2 sec left of video
            value = video.duration;
        }
        commit(types.UPDATE_VIDEO_PROGRESS, {
            id,
            value,
            timestamp: moment(),
            forBraindateCalculation: { progress: video.progress, duration: video.duration, lifeskillId }
        });

        persistVideoProgress(id);

        // --------------------------------------
        // Giving braindate
        // --------------------------------------

        const prevDoneCount = calculateCompletedRangeCount(video.duration, prevValue);
        const newDoneCount = calculateCompletedRangeCount(video.duration, value);
        const deltaCompletedPartsCount = newDoneCount - prevDoneCount;
        if (deltaCompletedPartsCount <= 0) {
            return;
        }

        for (let partIndex = prevDoneCount; partIndex < newDoneCount; partIndex++) {
            dispatch('registerBraindate', {
                context: 'video',
                lifeskillNumber: lifeskillId || (video.lifeskillIds || [])[0] || '00',
                videoId: video._id,
                partIndex,
                activity: 'watch'
            });
        }
    },

    async loadLifeskillsList({ commit, state }) {
        const lifeskills = await memomapsDataHelper.loadLifeskillsList();

        commit('SET_LIFESKILLS_LIST', lifeskills);

        const mappedLifeskills = lifeskills.map(entry => {
            return {
                _id: entry.id,
                number: entry.numbershape,
                numberOfVideos: entry.videocount,
                recommenedVideoIds: entry.videoLifeskillsList.map(x => x.video.youtubeId)
            };
        });

        commit(types.UPDATE_LIFESKILL_DATA, { lifeskills: mappedLifeskills });
    },

    async loadAllVideos({ commit, state }) {
        // Short circuit to prevent loading all videos in the app since it crashes
        // IOS apps
        if (state.loadStatus.didLoadAllVideos || state.loadStatus.isLoadingAllVideos) {
            return;
        }

        commit('updateLoadStatus', { isLoadingAllVideos: true });
        const videos = await memomapsDataHelper.loadAllVideos();
        commit(types.UPDATE_ALL_VIDEOS, videos);
        commit('updateLoadStatus', { didLoadAllVideos: true });
    },

    async loadVideosForLifeskill({ commit, state }, { lifeskillId }) {
        let videos = await memomapsDataHelper.loadVideosForLifeskill(lifeskillId);
        commit(types.UPDATE_ALL_VIDEOS, videos);
        let videoIds = [];
        for (const videoData of videos) {
            commit(types.UPDATE_VIDEO_DATA_WITH_DESCRIPTION, {
                videoId: videoData.id,
                description: videoData.description
            });
            videoIds.push(videoData.id);
        }
        commit(types.UPDATE_LIFESKILL_DATA, { lifeskillId: lifeskillId, data: { relatedVideos: videoIds } });
        return videos;
    },

    async loadVideosInProgress({ commit, getters, state }) {
        const videos = await memomapsDataHelper.loadVideosInProgress();
        commit('VIDEOS_IN_PROGRESS', videos);
        commit(types.UPDATE_ALL_VIDEOS, getters['inProgressVideoItems']);
    },

    async loadVideo({ commit, state }, videoId) {
        let video = (await memomapsDataHelper.loadVideo(videoId)) || {};
        video = memomapsDataHelper.videoMap({ video });
        commit(types.UPDATE_VIDEO_DATA, { videoId: video.id, data: video });
    },

    async loadRecommendedVideos({ commit, state, getters }) {
        const videos = await memomapsDataHelper.loadRecommendedVideos();
        commit('RECOMMENDED_VIDEOS', videos);
        commit(types.UPDATE_ALL_VIDEOS, getters['recommendedVideos']);
    },

    setDailyRequiredBraindates({ commit, state, dispatch }, payload) {
        commit(types.SET_DAILY_REQUIRED_BRAINDATES, payload);
        dispatch('updateSetting', { key: 'dailyRequiredBraindates', value: payload.value });
    },

    registerBraindate({ commit, state, getters, rootGetters, dispatch }, braindate) {
        if (!braindate.createdAt) {
            braindate.createdAt = Date.now();
        }
        commit(types.ADD_BRAINDATE, braindate);
        braindate.userId = store.getters['moduleAuth/profile'].id;
        if (braindate.isBonus) {
            braindate.language = 'isBonus'; // NOTE: temp: reuse prop (waiting with backend change)
        }

        // do mutation
        apolloClient
            .mutate({
                fetchPolicy: 'no-cache',
                mutation: CreateBraindateQ,
                variables: braindate
            })
            .then(result => {
                commit(types.SET_MEMOSTREAK, parseInt(result.data.createBraindate.query.memostreak, 10));
            })
            .catch(e => {
                const updateError = e.graphQLErrors.map(({ message }) => {
                    return message;
                });
            });

        const dailyRequiredBraindates = store.state.moduleApp.memolife.dailyRequiredBraindates;
        const weeklyRequiredBraindates = dailyRequiredBraindates * 7;
        const today = store.getters['moduleApp/braindatesForCurrentDay'].length;
        const thisWeek = store.getters['moduleApp/braindatesForCurrentWeek'].length;
        const dailyBraindateProgress = Math.min(1, today / dailyRequiredBraindates);
        const weeklyBraindateProgress = Math.min(1, thisWeek / weeklyRequiredBraindates);
        window.dataLayer.push({
            event: 'braindate',
            daily_braindate_progress: dailyBraindateProgress,
            weekly_braindate_progress: weeklyBraindateProgress
        });
        EventBus.$emit('newBraindate', braindate);

        // --------------------------------------
        // Awarding life key for onboarding video
        // --------------------------------------

        const { context, lifeskillNumber, videoId } = braindate;
        if (context !== 'video') {
            return;
        }

        const alreadyAwardedFirstVideoLifekey = rootGetters['moduleApp/lifekeys'].some(
            key => key.context === 'firstvideo'
        );
        if (!alreadyAwardedFirstVideoLifekey && videoId === settings.onboardingVideoDatabaseId) {
            dispatch('moduleApp/awardLifekey', { lifeskill: lifeskillNumber, context: 'firstvideo' }, { root: true });
        }

        const acquiredVideoBraindates = (getters.getVideoBraindatesMap[lifeskillNumber] || []).length;
        if (acquiredVideoBraindates >= 25) {
            const lifekeyContext = `${lifeskillNumber}-video1`;
            const alreadyAwarded = getters.lifekeys.some(
                key => key.lifeskill === lifeskillNumber && key.context === lifekeyContext
            );
            if (!alreadyAwarded) {
                dispatch('awardLifekey', { lifeskill: lifeskillNumber, context: lifekeyContext });
            }
        }
        if (acquiredVideoBraindates >= 50) {
            const lifekeyContext = `${lifeskillNumber}-video2`;
            const alreadyAwarded = getters.lifekeys.some(
                key => key.lifeskill === lifeskillNumber && key.context === lifekeyContext
            );
            if (!alreadyAwarded) {
                dispatch('awardLifekey', { lifeskill: lifeskillNumber, context: lifekeyContext });
            }
        }
    },

    // local state stuff
    setLifeskillSelectedVideo({ commit }, payload) {
        commit(types.SET_LIFESKILL_SELECTED_VIDEO, payload);
    },
    toggleLifeskillFollow({ commit, dispatch, state }, payload) {
        const following = !state.lifeskills[payload.lifeskillId].follow;

        commit(types.SET_LIFESKILL_MARKED, {
            lifeskillId: payload.lifeskillId,
            follow: following
        });

        apolloClient.mutate({
            fetchPolicy: 'no-cache',
            mutation: gql`
                mutation($lifeskill: String!, $following: Boolean!) {
                    followLifeskill(input: { _lifeskill: $lifeskill, _following: $following }) {
                        results {
                            lifeskill
                            following
                        }
                    }
                }
            `,
            variables: {
                lifeskill: payload.lifeskillId,
                following
            }
        });
    },

    toggleDebugButton({ commit, state }) {
        commit(types.SET_SHOW_DEBUG_BUTTON, { show: !state.localState.showDebugButton });
    },
    dbgToggleQuizCheatMode({ commit, state }) {
        commit(types.UPDATE_DEBUG_SETTINGS, { key: 'useQuizCheatMode', value: !state.debugSettings.useQuizCheatMode });
    },
    dbgSetDebugButtonPosition({ commit }, position) {
        commit(types.UPDATE_DEBUG_SETTINGS, { key: 'debugButtonPosition', value: position });
    },
    dbgToggleShowAllStandaloneCourses({ commit, state, rootState }) {
        commit(types.UPDATE_DEBUG_SETTINGS, {
            key: 'showAllStandaloneCourses',
            value: !state.debugSettings.showAllStandaloneCourses
        });
        updateAbility(rootState.moduleAuth.profile);
    },

    dbgToggleUnlockAllContent({ commit, state, rootState }) {
        commit(types.UPDATE_DEBUG_SETTINGS, { key: 'unlockAllContent', value: !state.debugSettings.unlockAllContent });
        updateAbility(rootState.moduleAuth.profile);
    },

    loadFavorites({ commit }) {
        return apolloClient
            .query({
                query: gql`
                    query {
                        followedLifeskillsList {
                            following
                            lifeskill
                        }
                    }
                `
            })
            .then(result => result.data)
            .then(data => {
                commit(types.SET_LIFESKILLS_MARKED, data.followedLifeskillsList);
            });
    },

    loadSettings({ commit, state }) {
        return apolloClient
            .query({
                query: gql`
                    query {
                        userSettingsList {
                            key
                            value
                        }
                    }
                `
            })
            .then(result => result.data)
            .then(data => {
                commit(types.UPDATE_APP_SETTINGS, {
                    settings: data.userSettingsList
                        .filter(setting => setting.key.startsWith('app:'))
                        .reduce((acc, curr, i) => {
                            const key = curr.key.split('app:')[1];
                            acc[key] = curr.value;
                            return acc;
                        }, {})
                });
            });
    },

    updateSetting({ commit, state }, { key, value }) {
        const prefix = 'app:';
        key = `${prefix}${key}`;
        return apolloClient.mutate({
            fetchPolicy: 'no-cache',
            mutation: gql`
                mutation($key: String!, $value: JSON!) {
                    updateUserSetting(input: { _key: $key, _value: $value }) {
                        results {
                            key
                            value
                        }
                    }
                }
            `,
            variables: { key, value }
        });
    },

    awardLifekey({ commit, state }, { lifeskill, context }) {
        commit(types.ADD_LIFEKEY, { lifeskill, context });

        EventBus.$emit('new-lifekey', {
            lifeskillId: lifeskill,
            continueToDashboard: context === 'firstvideo'
            // The first video is the memolife onboarding video.
            // We tell the new-lifekey popup to include a 'continue' button back to dashboard
        });

        return apolloClient.mutate({
            fetchPolicy: 'no-cache',
            mutation: gql`
                mutation($lifeskill: String!, $context: String!) {
                    createLifekey(input: { _lifeskill: $lifeskill, _context: $context }) {
                        results {
                            context
                            createdAt
                            lifeskill
                        }
                    }
                }
            `,
            variables: { lifeskill, context }
        });
    },

    loadLifekeys({ commit, state }) {
        return apolloClient
            .query({
                query: gql`
                    {
                        lifekeysList {
                            context
                            createdAt
                            lifeskill
                        }
                    }
                `
            })
            .then(result => commit(types.SET_LIFEKEYS, result.data.lifekeysList));
    }
};

var persistVideoProgress = debounce(
    videoId => {
        const userId = store.getters['moduleAuth/profile'].id;
        const video = store.getters['moduleApp/getVideoById'](videoId);
        apolloClient
            .mutate({
                fetchPolicy: 'no-cache',
                mutation: CreateOrUpdateVideoProgress,
                variables: {
                    videoId: video._id,
                    userId: userId,
                    progress: Math.floor(video.progress)
                }
            })
            .then(result => {})
            .catch(e => {
                const updateError = e.graphQLErrors.map(({ message }) => {
                    return message;
                });
            });
    },
    1500,
    { maxWait: 5000 }
);
